import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) return toast.error('Preencha todos os campos');
    if (password.length < 6) return toast.error('Senha deve ter pelo menos 6 caracteres');
    if (password !== confirmPassword) return toast.error('As senhas não coincidem');
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      toast.success('Senha redefinida com sucesso!');
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    } finally { setSubmitting(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 flex items-center justify-center p-6">
        <div className="bg-slate-800/60 rounded-2xl p-8 max-w-md w-full text-center border border-slate-700/50">
          <p className="text-red-400 font-medium">Link inválido</p>
          <button onClick={() => navigate('/')} className="mt-4 text-teal-400 hover:text-teal-300 text-sm">Voltar ao login</button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 flex items-center justify-center p-6">
        <div className="bg-slate-800/60 rounded-2xl p-8 max-w-md w-full text-center border border-slate-700/50" data-testid="reset-success">
          <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Senha Redefinida!</h2>
          <p className="text-slate-400">Redirecionando para o login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 flex flex-col items-center justify-center p-6">
      <div className="mb-8">
        <div className="bg-white rounded-2xl p-6 shadow-2xl shadow-teal-500/10">
          <img
            src="https://customer-assets.emergentagent.com/job_dubsync-3/artifacts/jj7xe7zi_Screenshot_3.jpeg.png"
            alt="DUBLEIROS STUDIOS" className="w-auto h-20 object-contain"
          />
        </div>
      </div>

      <div className="w-full max-w-md bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-xl" data-testid="reset-password-card">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="text-center mb-2">
            <h2 className="text-2xl font-bold text-white">Nova Senha</h2>
            <p className="text-slate-400 text-sm mt-1">Defina sua nova senha</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Nova Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-10 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500"
                placeholder="Mínimo 6 caracteres"
                data-testid="reset-new-password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirmar Nova Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500"
                placeholder="Repita a senha"
                data-testid="reset-confirm-password"
              />
            </div>
          </div>

          <button type="submit" disabled={submitting}
            className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white font-semibold py-3 rounded-lg transition-colors"
            data-testid="reset-submit"
          >
            {submitting ? 'Salvando...' : 'Salvar Nova Senha'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
